import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { FileText, ExternalLink, Receipt } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Bug-fix — "Estimate → Schedule Job → Calendar Appointment" workflow.
 *
 * Root cause: JobProfile fetched the job (which correctly carries `estimate_id`
 * from `POST /api/estimates/{id}/convert-to-job`) but never fetched the linked
 * estimate itself, so the UI appeared to show "no active estimate attached."
 *
 * This card:
 *   - Reads `job.estimate_id` (never `customer.active_estimate` — that would
 *     misattribute a job to the wrong estimate when a customer has several).
 *   - Fetches the exact `/api/estimates/{estimate_id}` document.
 *   - Renders estimate number, status, date, total, line-item preview.
 *   - Deep-links to the full estimate editor and the invoice (when raised).
 *   - Uses the label "Linked Estimate" — not "Active Estimate" — because a
 *     scheduled/converted/accepted estimate is still a permanent business
 *     record and belongs on the job even after status transitions.
 *
 * Silent-hide when the job has no `estimate_id` (jobs created directly, not
 * from an estimate). No layout shift for legacy non-estimate jobs.
 */

const STATUS_TONE = {
  draft:    "bg-zinc-100 text-zinc-700",
  sent:     "bg-blue-50 text-blue-700",
  viewed:   "bg-amber-50 text-amber-700",
  accepted: "bg-emerald-50 text-emerald-700",
  declined: "bg-rose-50 text-rose-700",
  expired:  "bg-zinc-100 text-zinc-500",
};

const fmtUsd = (n) =>
  (Number(n || 0)).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }); }
  catch { return "—"; }
};

export default function LinkedEstimateCard({ job }) {
  const nav = useNavigate();
  const estimateId = job?.estimate_id;
  const [est, setEst] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!estimateId) return;
    let mounted = true;
    (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const { data } = await api.get(`/estimates/${estimateId}`);
        if (mounted) setEst(data);
      } catch (e) {
        if (!mounted) return;
        if (e?.response?.status === 404) setNotFound(true);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [estimateId]);

  // Job wasn't created from an estimate — silent hide, no layout shift.
  if (!estimateId) return null;

  if (loading && !est) {
    return (
      <div className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp" data-testid="linked-estimate-loading">
        <div className="text-sm text-zinc-500">Loading linked estimate…</div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="bg-white border border-rose-200 rounded-xl p-6 card-crisp" data-testid="linked-estimate-missing">
        <div className="flex items-center gap-2 mb-1">
          <FileText className="w-5 h-5 text-rose-600" />
          <h2 className="font-heading text-xl font-bold">Linked estimate</h2>
        </div>
        <div className="text-sm text-rose-700">
          The originating estimate ({estimateId.slice(0, 8)}…) could not be loaded. It may have been deleted or belongs to a different tenant.
        </div>
      </div>
    );
  }

  if (!est) return null;

  const lineCount = (est.line_items || []).length;
  const preview = (est.line_items || []).slice(0, 3);

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp" data-testid="linked-estimate-card">
      <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <FileText className="w-5 h-5 text-[#FF5A00]" />
            <h2 className="font-heading text-xl font-bold">Linked estimate</h2>
          </div>
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <span className="font-mono text-[#FF5A00] font-semibold" data-testid="linked-estimate-number">
              {est.estimate_number || estimateId.slice(0, 8)}
            </span>
            <span
              className={`px-2 py-0.5 rounded uppercase tracking-widest font-mono ${STATUS_TONE[est.status] || "bg-zinc-100 text-zinc-700"}`}
              data-testid="linked-estimate-status"
            >
              {est.status || "draft"}
            </span>
            <span className="text-zinc-500" data-testid="linked-estimate-date">
              {fmtDate(est.created_at)}
            </span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Total</div>
          <div className="text-2xl font-bold text-zinc-900" data-testid="linked-estimate-total">
            {fmtUsd(est.total)}
          </div>
          {est.deposit_amount ? (
            <div className="text-[11px] text-zinc-500 mt-0.5">
              Deposit {fmtUsd(est.deposit_amount)}
            </div>
          ) : null}
        </div>
      </div>

      {lineCount > 0 && (
        <div className="mt-3 border-t border-zinc-100 pt-3" data-testid="linked-estimate-lines">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">
            Services · {lineCount} line{lineCount === 1 ? "" : "s"}
          </div>
          <ul className="text-sm space-y-1">
            {preview.map((li, i) => (
              <li key={i} className="flex justify-between gap-3">
                <span className="truncate">{li.name || `Item ${i + 1}`}</span>
                <span className="text-zinc-600 font-mono tabular-nums">
                  {li.quantity || 1} × {fmtUsd(li.unit_price)}
                </span>
              </li>
            ))}
            {lineCount > preview.length && (
              <li className="text-xs text-zinc-500 italic">
                +{lineCount - preview.length} more line{lineCount - preview.length === 1 ? "" : "s"}…
              </li>
            )}
          </ul>
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <Button
          variant="outline"
          onClick={() => nav(`/app/estimates/${estimateId}`)}
          data-testid="linked-estimate-open-btn"
        >
          <ExternalLink className="w-4 h-4 mr-1" /> Open estimate
        </Button>
        {est.converted_to_invoice_id ? (
          <Button
            onClick={() => nav(`/app/invoices/${est.converted_to_invoice_id}`)}
            data-testid="linked-estimate-invoice-btn"
            className="bg-[#FF5A00] hover:bg-[#E04F00] text-white"
          >
            <Receipt className="w-4 h-4 mr-1" /> View invoice
          </Button>
        ) : null}
      </div>
    </div>
  );
}
